/**
 * Mode-specific and persistent sidebar controls.
 *
 * Each workspace mode maps to a React component that renders its controls.
 * `SidebarControls` renders a compact wrapping row of icon buttons separated
 * by vertical bars between logical groups — matching the toolbar strip style.
 * `CollapsedSidebarControls` renders a compact icon column for the collapsed
 * dock strip.
 *
 * Sidebar controls surface persistent navigation, transform, cursor, and
 * selection actions for the active workspace.
 */

import { ArrowUpFromLine, Box, Copy, Eye, Globe, Trash2 } from "lucide-react";
import { createContext, useCallback, useContext } from "react";
import { getShortcutLabel } from "../app/keymap.ts";
import { selectionToLeafObjectIds } from "../app/objectSelection.ts";
import { useStore } from "../app/store.ts";
import type { TransformOrientation, WorkspaceMode } from "../app/storeTypes.ts";
import {
  getSnapPieOptions,
  isSnapMenuOptionEnabled,
  runSnapMenuOption,
  type SnapMenuState,
} from "./cursorSnapOptions.tsx";
import { useFloatingPanelStore } from "./floatingPanelStore.ts";
import { useHoldRepeat } from "./hooks/useHoldRepeat.ts";
import {
  FocusIcon,
  RedoIcon,
  SnapCursorToIcon,
  StarIcon,
  UndoIcon,
} from "./Icons.tsx";
import { OrientationIcon } from "./icons3d.tsx";
import { TRANSFORM_MODES } from "./ModeSwitcher.tsx";
import {
  COLOR_BORDER,
  COLOR_TEXT_DIM,
  DOCK_COLLAPSED_WIDTH,
  TOOL_FLYOUT_ICON_SIZE,
} from "./tokens.ts";
import { Tool } from "./tool/Tool.tsx";
import type { ToolVariant } from "./tool/ToolFlyout.tsx";
import { ToolSep } from "./tool/ToolSep.tsx";
import { ToolSurface } from "./tool/ToolSurface.tsx";
import { useHighlightOnChange } from "./tool/useHighlightOnChange.ts";

// ---------------------------------------------------------------------------
// Registry — each workspace mode maps to a controls component
// ---------------------------------------------------------------------------

type ModeControlsFn = () => React.ReactNode;

const MODE_CONTROLS: Record<WorkspaceMode, ModeControlsFn> = {
  object: ObjectModeControls,
  reference: ReferenceModeControls,
  pose: PoseModeControls,
  edit: EditModeControls,
  sculpt: SculptModeControls,
  sketch: SketchModeControls,
  render: RenderModeControls,
  inking: InkingModeControls,
};

// ---------------------------------------------------------------------------
// SidebarControlsContext — carries the collapsed state down to every control
// so each can adjust its layout or opt out entirely. The collapsed flag also
// drives the ambient ToolSurface edge (right when collapsed, bottom when
// expanded) so any Tool flyouts inside SidebarControls open away from the
// dock's free edge.
// ---------------------------------------------------------------------------

const SidebarControlsContext = createContext(false);
function useSidebarCollapsed() {
  return useContext(SidebarControlsContext);
}

/** Separator — orientation follows the sidebar layout (vertical row vs column). */
function ControlSep() {
  const collapsed = useSidebarCollapsed();
  return <ToolSep vertical={collapsed} />;
}

// ---------------------------------------------------------------------------
// TransformBeltControls — Move / Rotate / Scale tool group. Single button
// shows the active transform mode; flyout picks the others.
// ---------------------------------------------------------------------------

function TransformBeltControls({
  modes,
  highlight,
}: {
  modes?: typeof TRANSFORM_MODES;
  highlight?: boolean;
}) {
  const transformMode = useStore((s) => s.transformMode);
  const setTransformMode = useStore((s) => s.setTransformMode);
  const mode = useStore((s) => s.mode);
  const setMode = useStore((s) => s.setMode);

  const items = modes ?? TRANSFORM_MODES;
  const active = items.find((m) => m.key === transformMode) ?? items[0];

  const variants: ToolVariant[] = items.map((m) => ({
    key: m.key,
    icon: m.icon,
    label: m.label,
    shortcut: m.key[0].toUpperCase(),
    selected: m.key === transformMode,
    onActivate: () => {
      setTransformMode(m.key);
      if (mode !== "edit" && mode !== "transform") setMode("transform");
    },
  }));

  return (
    <Tool
      icon={active.icon}
      tip={`${active.label} (${active.key[0].toUpperCase()})`}
      active
      highlight={highlight}
      variants={variants}
      flyoutMinWidth={160}
    />
  );
}

// ---------------------------------------------------------------------------
// OrientationBeltControls — transform orientation picker (Global / Local /
// Normal / View).
// ---------------------------------------------------------------------------

const ORIENTATION_OPTIONS: {
  value: TransformOrientation;
  label: string;
  icon: React.ReactNode;
}[] = [
  { value: "global", label: "Global", icon: <Globe size={13} /> },
  { value: "local", label: "Local", icon: <Box size={13} /> },
  { value: "normal", label: "Normal", icon: <ArrowUpFromLine size={13} /> },
  { value: "view", label: "View", icon: <Eye size={13} /> },
];

function OrientationBeltControls() {
  const orientation = useStore((s) => s.transformOrientation);
  const setOrientation = useStore((s) => s.setTransformOrientation);
  const current = ORIENTATION_OPTIONS.find((o) => o.value === orientation)!;

  const variants: ToolVariant[] = ORIENTATION_OPTIONS.map((opt) => ({
    key: opt.value,
    icon: opt.icon,
    label: opt.label,
    selected: opt.value === orientation,
    onActivate: () => setOrientation(opt.value),
  }));

  return (
    <Tool
      icon={<OrientationIcon size={13} />}
      tip={`Orientation: ${current.label}`}
      variants={variants}
      flyoutMinWidth={160}
    />
  );
}

// ---------------------------------------------------------------------------
// SidebarControls — unified dock footer. Horizontal (row) when expanded,
// vertical (column) when collapsed. Provides SidebarControlsContext + a
// matching ToolSurface edge so every Tool inside renders identically and
// its flyouts open away from the dock.
// ---------------------------------------------------------------------------

export function SidebarControls() {
  const workspaceMode = useStore((s) => s.workspaceMode);
  const collapsed = useFloatingPanelStore((s) => s.dockCollapsed);
  const ModeControls = MODE_CONTROLS[workspaceMode];

  return (
    <SidebarControlsContext.Provider value={collapsed}>
      <ToolSurface edge={collapsed ? "right" : "bottom"}>
        <div
          style={{
            display: "flex",
            flexDirection: collapsed ? "column" : "row",
            flexWrap: collapsed ? undefined : "wrap",
            alignItems: "center",
            gap: collapsed ? 2 : 4,
            padding: collapsed ? "6px 0 12px" : "0 8px",
            paddingBottom: collapsed
              ? undefined
              : "calc(env(safe-area-inset-bottom, 0px) * 0.5)",
            minHeight: collapsed ? undefined : `${DOCK_COLLAPSED_WIDTH}px`,
            borderTop: `0.5px solid ${COLOR_BORDER}`,
            flexShrink: 0,
          }}
        >
          <ModeControls />
          <PersistentControls />
          <Cursor3DControlsHost />
          <UndoRedoControls />
        </div>
      </ToolSurface>
    </SidebarControlsContext.Provider>
  );
}

/**
 * Gates the Cursor3DControls to workspace modes where a selection / transform
 * makes sense. Hiding it in reference / sketch / sculpt / render keeps the
 * sidebar compact in contexts where the cursor has no effect.
 */
function Cursor3DControlsHost() {
  const workspaceMode = useStore((s) => s.workspaceMode);
  const allowed =
    workspaceMode === "object" ||
    workspaceMode === "edit" ||
    workspaceMode === "pose";
  if (!allowed) return null;
  return <Cursor3DControls />;
}

// ---------------------------------------------------------------------------
// 3D Cursor controls — Blender-style cursor operations.
//
// A single tool group that mirrors the Shift+S pie menu: combines
// "Cursor to …" and "Selection / Origins to Cursor" into one flyout, with
// a subtle divider between the two groups. Cursor selection (gizmo
// targeting) is handled by tapping the cursor in the viewport.
// ---------------------------------------------------------------------------

function Cursor3DSnapBeltControl() {
  const workspaceMode = useStore((s) => s.workspaceMode);
  const selectedObjectCount = useStore((s) => s.selectedObjectTargets.length);
  const editMode = useStore((s) => s.editMode);
  const poseMode = useStore((s) => s.poseMode);

  const snapState: SnapMenuState = {
    workspaceMode,
    selectedObjectCount,
    editMode,
    poseMode,
  };
  const items = getSnapPieOptions(snapState);

  const variants: ToolVariant[] = items.map((opt) => ({
    key: opt.id,
    icon: opt.renderIcon(TOOL_FLYOUT_ICON_SIZE - 1),
    label: opt.pieLabel,
    shortcut: opt.shortcut,
    disabled: !isSnapMenuOptionEnabled(opt, snapState),
    group: opt.group,
    iconColor: COLOR_TEXT_DIM,
    onActivate: () => runSnapMenuOption(opt),
  }));

  return (
    <Tool
      icon={<SnapCursorToIcon size={13} />}
      tip="3D Cursor"
      variants={variants}
      flyoutMinWidth={200}
    />
  );
}

function Cursor3DControls() {
  return (
    <>
      <ControlSep />
      <Cursor3DSnapBeltControl />
    </>
  );
}

// ---------------------------------------------------------------------------
// Undo / Redo controls — pinned at the end of the sidebar dock. Hold-to-repeat
// matches the OS keyboard repeat convention.
// ---------------------------------------------------------------------------

function UndoRedoControls() {
  const undo = useStore((s) => s.undo);
  const redo = useStore((s) => s.redo);
  const canUndo = useStore((s) => s.canUndo);
  const canRedo = useStore((s) => s.canRedo);
  const undoHold = useHoldRepeat(undo, canUndo);
  const redoHold = useHoldRepeat(redo, canRedo);

  return (
    <>
      <ControlSep />
      <Tool
        icon={<UndoIcon size={13} />}
        tip={`Undo (${getShortcutLabel("undo")})`}
        disabled={!canUndo}
        onActivate={undoHold.onClick}
        onPointerDown={undoHold.onPointerDown}
        onPointerUp={undoHold.onPointerUp}
        onPointerCancel={undoHold.onPointerCancel}
      />
      <Tool
        icon={<RedoIcon size={13} />}
        tip={`Redo (${getShortcutLabel("redo")})`}
        disabled={!canRedo}
        onActivate={redoHold.onClick}
        onPointerDown={redoHold.onPointerDown}
        onPointerUp={redoHold.onPointerUp}
        onPointerCancel={redoHold.onPointerCancel}
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// Persistent controls — Duplicate / Delete (always visible in modes that
// have a meaningful selection).
// ---------------------------------------------------------------------------

function PersistentControls() {
  const hasSelection = useStore((s) => s.selectedObjectTargets.length > 0);
  const workspaceMode = useStore((s) => s.workspaceMode);
  const editMode = workspaceMode === "edit";

  const handleDuplicate = useCallback(() => {
    useStore.getState().duplicate();
  }, []);

  const handleDelete = useCallback(() => {
    const s = useStore.getState();
    if (editMode) {
      s.setDeleteMenuOpen(true);
      return;
    }
    const selected = selectionToLeafObjectIds(
      s.blendFiles,
      s.collections,
      s.selectedObjectTargets,
    );
    if (selected.size === 0) return;
    s.pushUndo();
    const sceneIds = new Set(s.sceneObjects.keys());
    for (const id of selected) {
      if (sceneIds.has(id)) s.removeSceneObject(id);
      else s.toggleVisibility(id);
    }
  }, [editMode]);

  const showActions =
    workspaceMode === "object" ||
    workspaceMode === "pose" ||
    workspaceMode === "edit";
  if (!showActions) return null;

  return (
    <>
      <ControlSep />
      <Tool
        icon={<Copy size={13} />}
        tip={`Duplicate (${getShortcutLabel("duplicate")})`}
        disabled={!hasSelection}
        onActivate={handleDuplicate}
      />
      <Tool
        icon={<Trash2 size={13} />}
        tip={`Delete (${getShortcutLabel("deleteObjects") ?? "X"})`}
        disabled={!hasSelection}
        onActivate={handleDelete}
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// Mode-specific controls
//
// Each returns a fragment of Tool icons. Groups are separated by ControlSep.
// No labels — the icons + tooltips are self-explanatory.
// ---------------------------------------------------------------------------

function ObjectModeControls() {
  const hasSelection = useStore((s) => s.selectedObjectTargets.length > 0);
  const hasFavorites = useStore((s) => s.favoriteShapeKeys.size > 0);
  const focusSelection = useStore((s) => s.focusSelection);

  const highlightTransform = useHighlightOnChange(hasSelection);

  return (
    <>
      {hasFavorites && (
        <Tool
          icon={<StarIcon size={13} />}
          tip="Favorites"
          onActivate={() => window.dispatchEvent(new Event("toggle-favorites"))}
        />
      )}

      {hasSelection && (
        <>
          <Tool
            icon={<FocusIcon size={13} />}
            tip="Focus"
            onActivate={focusSelection}
          />
          <ControlSep />
          <TransformBeltControls highlight={highlightTransform} />
          <OrientationBeltControls />
        </>
      )}
    </>
  );
}

function ReferenceModeControls() {
  return null;
}

function PoseModeControls() {
  const POSE_TRANSFORMS = TRANSFORM_MODES.filter((m) => m.key !== "scale");

  return <TransformBeltControls modes={POSE_TRANSFORMS} />;
}

function EditModeControls() {
  const mode = useStore((s) => s.mode);
  const canTransform = mode === "edit";

  const highlightTransform = useHighlightOnChange(canTransform);

  return (
    <>
      {canTransform && (
        <>
          <TransformBeltControls highlight={highlightTransform} />
          <OrientationBeltControls />
        </>
      )}
    </>
  );
}

function SculptModeControls() {
  return null;
}

function SketchModeControls() {
  return null;
}

function RenderModeControls() {
  return null;
}

function InkingModeControls() {
  return null;
}
